import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { DialogHeader, DialogBody } from "components/common/dialogs/GenericDialog";
import { useLocale } from "lib/hooks/useLocale";
import Price from "components/common/Price";
import { addHttpIfMissing, classNames, downloadFileFromUrl } from "lib/utils/helpers";
import defaultPackageImage from "assests/images/package.png";
import { MapPin, FileText } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { formatDateTimeValue, parseLocationValue } from "lib/utils/formatters";
import {
  ItemPropertiesDialogProps,
  ItemPropertyFileValue,
  ItemPropertyImageValue,
  ItemPropertyObjectValue,
  ItemPropertyValue,
} from "lib/types/itemPropertiesDialog";

/**
 * The sticky header fades in over this scroll distance, rather than snapping at a single
 * pixel threshold. A hard threshold flickers: sub-pixel scroll jitter right at that boundary (momentum/trackpad scrolling) flips the state back and forth. Ramping opacity over a small window means jitter only nudges the opacity slightly instead of toggling it.
 */
const HEADER_FADE_START_DISTANCE = 0; // px: the title block's bottom edge must have scrolled past the top of the scroll container by this much → header starts fading in
const HEADER_FADE_END_DISTANCE = 10; // px: title block's bottom edge scrolled past the top by this much → header fully visible

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const BASE_ITEM_PROPERTY_CLASSNAMES = "text-xs opacity-60 leading-4 break-words min-w-0";

const PropertyImageThumbnail = ({ src, onClick }: { src: string; onClick?: () => void }) => {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");
  const imgRef = useRef<HTMLImageElement>(null);

  // A cached image can finish loading before React attaches onLoad, so check once it's mounted. It's a layout effect so this runs before the first paint - a cached image shows at once instead of flashing the placeholder. Callers key this by `src`, so a new image remounts with fresh state instead of this effect having to reset it:
  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setStatus("loaded");
  }, []);

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded-xl border border-gray-light bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-carbon-dark/40">
      {status === "loading" && (
        <span aria-hidden="true" className="absolute inset-0 animate-pulse bg-zinc-200" />
      )}
      <img
        ref={imgRef}
        src={status === "error" ? defaultPackageImage : src}
        alt=""
        loading="lazy"
        decoding="async"
        onLoad={() => setStatus((prev) => (prev === "error" ? prev : "loaded"))}
        onError={() => setStatus("error")}
        className={classNames(
          "absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ease-out",
          status === "loading" ? "opacity-0" : "opacity-100",
        )}
      />
    </button>
  );
};

const PropertyFileThumbnail = ({
  src,
  fileName,
  label,
}: {
  src: string;
  fileName?: string;
  label: string;
}) => (
  <button
    type="button"
    onClick={() => downloadFileFromUrl(src, fileName)}
    className="inline-flex items-center gap-1 text-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
    <Icon IconComponent={FileText} size="xs" />
    <span className="underline">{label}</span>
  </button>
);

type PropertyRendererContext = {
  t: (key: string) => string;
  onImageClick?: (images: string[], startIndex: number) => void;
};

type PropertyRenderer = (value: ItemPropertyObjectValue, ctx: PropertyRendererContext) => ReactNode | null;

const renderImageProperty: PropertyRenderer = (value, { onImageClick }) => {
  const imageOrImages = value as ItemPropertyImageValue | string;
  if (Array.isArray(imageOrImages)) {
    const images = imageOrImages.filter((item) => Boolean(item.image_src));
    if (images.length === 0) return null;
    const imageSrcs = images.map((image) => image.image_src);
    return (
      <div className="flex flex-wrap gap-2">
        {images.map((item, index) => (
          <PropertyImageThumbnail
            key={`${index}-${item.image_src}`}
            src={item.image_src}
            onClick={() => onImageClick?.(imageSrcs, index)}
          />
        ))}
      </div>
    );
  }

  const imageValue = imageOrImages;
  if (imageValue === "" || typeof imageValue !== "string") return null;
  return (
    <div className="flex flex-wrap gap-2">
      <PropertyImageThumbnail src={imageValue} onClick={() => onImageClick?.([imageValue], 0)} />
    </div>
  );
};

const renderFileProperty: PropertyRenderer = (value, { t }) => {
  const fileOrFiles = value as ItemPropertyFileValue;
  if (Array.isArray(fileOrFiles)) {
    const files = fileOrFiles.filter((item) => Boolean(item.file_src));
    if (files.length === 0) return null;
    return (
      <div className="flex flex-col gap-1 items-start">
        {files.map((item, index) => (
          <PropertyFileThumbnail
            key={index}
            src={item.file_src}
            fileName={item.file_name}
            label={t("uploaded_file")}
          />
        ))}
      </div>
    );
  }

  const fileSrc = fileOrFiles;
  if (fileSrc === "" || typeof fileSrc !== "string") return null;
  return (
    <div className="flex flex-col gap-1 items-start">
      <PropertyFileThumbnail src={fileSrc} label={t("uploaded_file")} />
    </div>
  );
};

const renderLocationProperty: PropertyRenderer = (value, { t }) => {
  const coords = parseLocationValue(value);
  if (!coords) {
    return <span className={BASE_ITEM_PROPERTY_CLASSNAMES}>{t("invalid_location_value")}</span>;
  }

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
  return (
    <button
      type="button"
      onClick={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
      className="inline-flex items-center gap-1 rounded-full border border-gray-light px-2.5 py-1 text-xs opacity-70 transition-opacity hover:opacity-100">
      <Icon IconComponent={MapPin} size="xs" />
      {t("view_location")}
    </button>
  );
};

const renderDateTimeProperty: PropertyRenderer = (value) => {
  const formatted = formatDateTimeValue(value);
  const fallback = typeof value === "string" || typeof value === "number" ? value : "";
  return <span className={BASE_ITEM_PROPERTY_CLASSNAMES}>{formatted ?? fallback}</span>;
};

const renderLinkProperty: PropertyRenderer = (value) => {
  if (typeof value !== "string") return null;
  return (
    <a
      href={addHttpIfMissing(value)}
      rel="noopener noreferrer"
      target="_blank"
      className="text-blue-800 text-xs underline flex flex-wrap gap-1 items-center leading-4 break-all">
      {value}
    </a>
  );
};

const renderDefaultProperty: PropertyRenderer = (value) => (
  <span className={BASE_ITEM_PROPERTY_CLASSNAMES}>
    {typeof value === "string" || typeof value === "number" ? value : ""}
  </span>
);

const PROPERTY_RENDERERS: Record<string, PropertyRenderer> = {
  image: renderImageProperty,
  file: renderFileProperty,
  location: renderLocationProperty,
  date_time: renderDateTimeProperty,
  link: renderLinkProperty,
};

const renderPropertyValue = (
  value: ItemPropertyValue,
  t: (key: string) => string,
  onImageClick?: (images: string[], startIndex: number) => void,
): ReactNode => {
  if (typeof value !== "object" || value === null) {
    return <span className={BASE_ITEM_PROPERTY_CLASSNAMES}>{String(value)}</span>;
  }

  const renderer = PROPERTY_RENDERERS[value.type] ?? renderDefaultProperty;
  return renderer(value.value, { t, onImageClick });
};

const ItemPropertiesDialog: React.FC<ItemPropertiesDialogProps> = React.memo(
  ({ itemDetails, itemProperties, onImageClick }) => {
    const { t } = useLocale();
    const scrollContainerRef = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const titleBlockRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      // Handling the opacity of the header and the title block:
      const root = scrollContainerRef.current;
      const titleBlock = titleBlockRef.current;
      if (!root || !titleBlock) return;

      const handleScroll = () => {
        // Measured on every scroll (not cached) so it stays correct if the title wraps or the image above it loads late:
        const distancePastTop = root.getBoundingClientRect().top - titleBlock.getBoundingClientRect().bottom;
        const progress = clamp01(
          (distancePastTop - HEADER_FADE_START_DISTANCE) /
            (HEADER_FADE_END_DISTANCE - HEADER_FADE_START_DISTANCE),
        );

        if (headerRef.current) {
          headerRef.current.style.opacity = String(progress);
          headerRef.current.style.pointerEvents = progress > 0 ? "auto" : "none";
        }
        titleBlock.style.opacity = String(1 - progress);
      };
      handleScroll();
      root.addEventListener("scroll", handleScroll, { passive: true });
      return () => root.removeEventListener("scroll", handleScroll);
    }, [itemDetails.image]);

    return (
      <>
        <DialogHeader
          ref={headerRef}
          style={{ opacity: 0, pointerEvents: "none" }}
          className="!px-5 py-4 transition-opacity duration-200 ease-out !bg-transparent !bg-[linear-gradient(180deg,#FFF_77.86%,rgba(255,255,255,0.00)_100%)]">
          <div className="flex w-full items-start justify-between gap-4 rounded-t-3xl">
            <div className="flex min-w-0 items-start gap-3">
              {itemDetails.image && (
                <img
                  loading="lazy"
                  src={itemDetails.image}
                  alt={itemDetails.item_title}
                  className="h-10 w-10 flex-shrink-0 rounded-lg border border-gray-light object-cover"
                />
              )}
              <div className="min-w-0">
                <h2 className="text-normal text-carbon-dark line-clamp-1">{itemDetails.item_title}</h2>
                {Boolean(itemDetails.variant_title) && itemDetails.variant_title !== "Default Title" && (
                  <p className="text-text-xs-regular text-zinc-500 line-clamp-1">
                    {itemDetails.variant_title}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              <Price
                total={itemDetails.finalPrice}
                compareAt={itemDetails.finalCompareAtPrice}
                orientation="horizontal"
                size="lg"
              />
            </div>
          </div>
        </DialogHeader>

        <DialogBody ref={scrollContainerRef} className="!p-0 min-h-0 rounded-t-2xl bg-zinc-50">
          {itemDetails.image && (
            <div className="flex justify-center">
              <div className="relative flex h-48 w-full items-center justify-center overflow-hidden p-3">
                {/* Background blur */}
                <img
                  src={itemDetails.image}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full scale-110 object-cover blur-md"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = defaultPackageImage;
                  }}
                />

                <button
                  type="button"
                  onClick={() => onImageClick?.([itemDetails.image as string], 0)}
                  className="relative z-10 h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-carbon-dark/40">
                  <img
                    src={itemDetails.image}
                    alt={itemDetails.item_title}
                    className="h-full w-fit object-contain rounded-md"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = defaultPackageImage;
                    }}
                  />
                </button>
              </div>
            </div>
          )}

          <div
            ref={titleBlockRef}
            className="grid grid-cols-[1fr_100px] gap-4 justify-between items-start px-4 pb-2 pt-3 transition-opacity duration-200 ease-out">
            <div className="text-start">
              <h2 className="text-normal">{itemDetails.item_title}</h2>
              {Boolean(itemDetails.variant_title) && itemDetails.variant_title !== "Default Title" && (
                <p className="text-text-sm-regular text-zinc-500">{itemDetails.variant_title}</p>
              )}
            </div>
            <div className="flex flex-col items-end justify-between gap-0.5">
              <Price
                total={itemDetails.finalPrice}
                compareAt={itemDetails.finalCompareAtPrice}
                orientation="horizontal"
                size="lg"
              />
            </div>
          </div>

          {/* Properties section */}
          <div className="flex w-full flex-col px-4 pb-6 pt-2">
            <div className="flex flex-col divide-y-[2px] divide-dotted divide-[2px] divide-gray-light rounded-xl border border-gray-light bg-white">
              {Object.entries(itemProperties ?? {}).map(([key, value]) => (
                <div
                  key={key}
                  className="grid grid-cols-[100px_1fr] items-start justify-between gap-4 px-4 py-2.5">
                  <div className="leading-4 text-xs">{key}:</div>
                  <div className="leading-4 min-w-0 flex flex-wrap gap-1">
                    {renderPropertyValue(value, t, onImageClick)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogBody>
      </>
    );
  },
);

export default ItemPropertiesDialog;
